/**
 * Helpdesk API client for ticketing system integration
 */
import { apiClient } from './client';

/** Helpdesk providers this dashboard can read from. */
export type HelpdeskProviderId = 'zoho' | 'servicenow' | 'zendesk' | 'jira' | 'itsm_mcp';

export interface CustomerAccountBinding {
  account_id: string;
  account_name: string;
  customer_id: string;
}

export interface HelpdeskConfig {
  id: string;
  name: string;
  provider: HelpdeskProviderId;
  region?: string;
  api_base_url?: string;
  is_active: boolean;
  last_sync_at?: string;
  sync_status?: string;
  sync_error?: string;
  created_at: string;
  updated_at?: string;
}

export interface HelpdeskConfigCreate {
  name: string;
  provider: HelpdeskProviderId;
  api_base_url?: string;
  client_id?: string;
  client_secret?: string;
  access_token?: string;
  refresh_token?: string;
  grant_token?: string;  // Grant token for simplified OAuth flow
  redirect_uri?: string;
  region?: string;
  token_expires_at?: string;
  customer_accounts?: CustomerAccountBinding[];
  config_data?: Record<string, any>;
}

export interface TestConnectionRequest {
  provider: HelpdeskProviderId;
  client_id?: string;
  client_secret?: string;
  access_token?: string;
  region?: string;
  api_base_url?: string;
}

export interface Account {
  id: string;
  name: string;
  website?: string;
  phone?: string;
}

export interface TicketSummary {
  mapped: boolean;
  total: number;
  by_priority: Record<string, number>;
  by_status: Record<string, number>;
  open_count: number;
  resolved_count: number;
  closed_count: number;
  urgent_count: number;
  high_count: number;
}

export interface Ticket {
  id: string;
  ticket_number?: string;
  subject?: string;
  priority?: string;
  status?: string;
  contact_email?: string;
  assignee_name?: string;
  created_time?: string;
  modified_time?: string;
  web_url?: string;
}

export interface TicketDetail extends Ticket {
  external_id: string;
  config_id: string;
  description?: string;
  resolution?: string;
  status_type?: string;
  classification?: string;
  category?: string;
  sub_category?: string;
  tags?: string[];
  contact_id?: string;
  contact_name?: string;
  contact_phone?: string;
  assignee_id?: string;
  assignee_email?: string;
  account_id?: string;
  account_name?: string;
  department_id?: string;
  department_name?: string;
  team_id?: string;
  team_name?: string;
  product_id?: string;
  closed_time?: string;
  due_date?: string;
  customer_response_time?: string;
  channel?: string;
  comment_count?: number;
  attachment_count?: number;
  time_entry_count?: number;
  approval_count?: number;
  thread_count?: number;
  task_count?: number;
  custom_fields?: Record<string, any>;
  last_synced_at?: string;
}

export interface TicketComment {
  id: string;
  content: string;
  author?: string;
  created_time?: string;
  is_public?: boolean;
}

export interface TicketAttachment {
  id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  created_time?: string;
  modified_time?: string;
}

export interface TicketTask {
  id: string;
  subject?: string;
  description?: string;
  status?: string;
  priority?: string;
  due_date?: string;
  assignee_id?: string;
  assignee_name?: string;
  created_time?: string;
  modified_time?: string;
}

export interface TicketHistory {
  id: string;
  event_time?: string;
  actor?: string;
  action?: string;
  field_name?: string;
  previous_value?: string;
  current_value?: string;
}

export interface TicketTimeEntry {
  id: string;
  owner_id?: string;
  owner_name?: string;
  hours_worked: number;
  minutes_worked: number;
  execution_date?: string;
  description?: string;
  cost_per_hour?: number;
  bill_status?: string;
  created_time?: string;
  modified_time?: string;
}

export interface ConnectionHealth {
  config_id: string;
  name: string;
  provider: string;
  healthy: boolean;
  reason?: string | null;
  last_sync_at?: string | null;
  last_success_at?: string | null;
  consecutive_failures: number;
  sync_error?: string | null;
}

export interface SyncLog {
  id: string;
  config_id: string;
  sync_type: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  status: string;
  tickets_fetched: number;
  tickets_created: number;
  tickets_updated: number;
  error_message?: string;
  created_at: string;
}

export interface HelpdeskSchedule {
  id: string;
  config_id: string;
  name: string;
  description?: string;
  enabled: boolean;
  interval_minutes?: number;
  cron_expression?: string;
  sync_type: string;
  fetch_limit: number;
  last_run_at?: string;
  next_run_at?: string;
  last_sync_status?: string;
  last_error?: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

export interface HelpdeskScheduleCreate {
  config_id: string;
  name: string;
  description?: string;
  enabled?: boolean;
  interval_minutes?: number;
  cron_expression?: string;
  sync_type?: string;
  fetch_limit?: number;
}

export interface HelpdeskScheduleUpdate {
  name?: string;
  description?: string;
  enabled?: boolean;
  interval_minutes?: number;
  cron_expression?: string;
  sync_type?: string;
  fetch_limit?: number;
}

export const helpdeskApi = {
  // Configuration management
  async listConfigs(): Promise<HelpdeskConfig[]> {
    const response = await apiClient.get('/helpdesk/configs');
    return response.data;
  },

  async getConfig(configId: string): Promise<HelpdeskConfig> {
    const response = await apiClient.get(`/helpdesk/configs/${configId}`);
    return response.data;
  },

  async createConfig(data: HelpdeskConfigCreate): Promise<HelpdeskConfig> {
    const response = await apiClient.post('/helpdesk/configs', data);
    return response.data;
  },

  async updateConfig(configId: string, data: Partial<HelpdeskConfigCreate>): Promise<HelpdeskConfig> {
    const response = await apiClient.put(`/helpdesk/configs/${configId}`, data);
    return response.data;
  },

  async deleteConfig(configId: string): Promise<void> {
    await apiClient.delete(`/helpdesk/configs/${configId}`);
  },

  // OAuth flow
  async generateAuthUrl(data: {
    provider: HelpdeskProviderId;
    client_id: string;
    redirect_uri: string;
    region?: string;
  }): Promise<{ auth_url: string; state?: string }> {
    const response = await apiClient.post('/helpdesk/generate-auth-url', data);
    return response.data;
  },

  async exchangeCode(data: {
    provider: HelpdeskProviderId;
    client_id: string;
    client_secret: string;
    code: string;
    redirect_uri?: string;
    grant_type?: 'authorization_code' | 'authorization_token';
    region?: string;
  }): Promise<{
    success: boolean;
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    message?: string;
    error?: string;
  }> {
    const response = await apiClient.post('/helpdesk/exchange-code', data);
    return response.data;
  },

  // Connection testing
  async testConnection(data: TestConnectionRequest): Promise<{ success: boolean; message: string; error?: string }> {
    const response = await apiClient.post('/helpdesk/test-connection', data);
    return response.data;
  },

  // Account management
  async fetchAccounts(data: TestConnectionRequest): Promise<Account[]> {
    const response = await apiClient.post('/helpdesk/fetch-accounts', data);
    return response.data;
  },

  async getAccounts(configId: string): Promise<Account[]> {
    const response = await apiClient.get(`/helpdesk/configs/${configId}/accounts`);
    return response.data;
  },

  /** Whether each active integration is still collecting. */
  async getConnectionHealth(): Promise<ConnectionHealth[]> {
    const response = await apiClient.get('/helpdesk/health');
    return response.data;
  },

  /** Accounts already seen on cached tickets and saved links; no provider call. */
  async getKnownAccounts(configId: string): Promise<Account[]> {
    const response = await apiClient.get(`/helpdesk/configs/${configId}/known-accounts`);
    return response.data;
  },

  // Ticket synchronization
  async syncTickets(configId: string, fullSync: boolean = false, limit?: number): Promise<any> {
    const response = await apiClient.post(`/helpdesk/configs/${configId}/sync`, {
      full_sync: fullSync,
      limit,
    });
    return response.data;
  },

  async getCustomerAccounts(configId: string): Promise<CustomerAccountBinding[]> {
    return (await apiClient.get(`/helpdesk/configs/${configId}/customer-accounts`)).data;
  },

  async saveCustomerAccounts(configId: string, bindings: CustomerAccountBinding[]): Promise<CustomerAccountBinding[]> {
    return (await apiClient.put(`/helpdesk/configs/${configId}/customer-accounts`, bindings)).data;
  },

  // Ticket data
  async getTicketSummary(customerId: string, configId?: string): Promise<TicketSummary> {
    const params = { customer_id: customerId, ...(configId ? { config_id: configId } : {}) };
    const response = await apiClient.get('/helpdesk/tickets/summary', { params });
    return response.data;
  },

  async listTickets(params: {
    customer_id: string;
    config_id?: string;
    priority?: string;
    status?: string;
    status_type?: string;
    skip?: number;
    limit?: number;
  }): Promise<Ticket[]> {
    const response = await apiClient.get('/helpdesk/tickets', { params });
    return response.data;
  },

  async getTicket(ticketId: string, customerId: string): Promise<TicketDetail> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketFullDetails(ticketId: string, customerId: string): Promise<any> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/full`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketComments(ticketId: string, customerId: string): Promise<TicketComment[]> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/comments`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketAttachments(ticketId: string, customerId: string): Promise<TicketAttachment[]> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/attachments`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketTasks(ticketId: string, customerId: string): Promise<TicketTask[]> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/tasks`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketHistory(ticketId: string, customerId: string): Promise<TicketHistory[]> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/history`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketTimeEntries(ticketId: string, customerId: string): Promise<TicketTimeEntry[]> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/time-entries`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketResolution(ticketId: string, customerId: string): Promise<any> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/resolution`, { params: { customer_id: customerId } });
    return response.data;
  },

  async getTicketContactProfiles(ticketId: string, customerId: string): Promise<any[]> {
    const response = await apiClient.get(`/helpdesk/tickets/${ticketId}/contact-profiles`, { params: { customer_id: customerId } });
    return response.data;
  },

  // Sync logs
  async getSyncLogs(configId: string, skip?: number, limit?: number): Promise<SyncLog[]> {
    const params: any = {};
    if (skip !== undefined) params.skip = skip;
    if (limit !== undefined) params.limit = limit;
    const response = await apiClient.get(`/helpdesk/configs/${configId}/sync-logs`, { params });
    return response.data;
  },

  // Schedule management
  async listSchedules(configId: string): Promise<HelpdeskSchedule[]> {
    const response = await apiClient.get(`/helpdesk/configs/${configId}/schedules`);
    return response.data;
  },

  async getSchedule(scheduleId: string): Promise<HelpdeskSchedule> {
    const response = await apiClient.get(`/helpdesk/schedules/${scheduleId}`);
    return response.data;
  },

  async createSchedule(configId: string, data: HelpdeskScheduleCreate): Promise<HelpdeskSchedule> {
    const response = await apiClient.post(`/helpdesk/configs/${configId}/schedules`, data);
    return response.data;
  },

  async updateSchedule(scheduleId: string, data: HelpdeskScheduleUpdate): Promise<HelpdeskSchedule> {
    const response = await apiClient.put(`/helpdesk/schedules/${scheduleId}`, data);
    return response.data;
  },

  async deleteSchedule(scheduleId: string): Promise<void> {
    await apiClient.delete(`/helpdesk/schedules/${scheduleId}`);
  },

  async toggleSchedule(scheduleId: string): Promise<HelpdeskSchedule> {
    const response = await apiClient.post(`/helpdesk/schedules/${scheduleId}/toggle`);
    return response.data;
  },
};
